import {
  ACTIVE_STATUSES,
  CONVERSATION_FOLDERS,
  CONVERSATION_LIMITS,
  CONVERSATION_PRIORITIES,
  GROUP_FILTERS,
  WINDOW_FILTERS,
  type ActiveStatus,
  type ConversationFolder,
  type GroupFilter,
  type ConversationListQuery,
  type ConversationPriority,
  type WindowFilter,
} from '../contract';
import { QUEUE_KINDS, type QueueKind } from '../distribution-contract';
import { SLA_FILTERS, type SlaFilter } from '../sla-contract';

/**
 * The state of `/conversations` in the URL (spec 008 §7.2), in English:
 * `?conversationId=&folder=unassigned|all|mentions|snoozed|resolved|queue|waiting&queue=&status=open|pending&channel=&label=&assignee=&team=&priority=&sla=at_risk|breached&window=closing&unread=true&q=&sort=waiting|sla`.
 * The defaults stay out: "Minhas" and the most recent first. `queue` is the folder of a queue
 * with `folder=queue`, and a filter in the other folders (spec 009, rule 28).
 */
export interface InboxSearch {
  conversationId?: string;
  folder?: Exclude<ConversationFolder, 'mine'>;
  status?: ActiveStatus;
  channel?: string;
  label?: string;
  assignee?: string;
  team?: string;
  queue?: string;
  /** The recepção of `folder=triage` (spec 023). */
  reception?: string;
  priority?: ConversationPriority;
  /** Spec 010, rule 27: the conversations at risk or breached. */
  sla?: SlaFilter;
  /** Spec 065, R7: the 24-hour window of a waiting contact closes within two hours. */
  window?: WindowFilter;
  /** Spec 024, rule 28: a filter of the Groups folder. */
  group?: GroupFilter;
  unread?: true;
  q?: string;
  sort?: 'waiting' | 'sla';
  /**
   * Spec 048: the area (the kind of the queues) the list shows; `all` says "every area" when
   * the member would otherwise start in their own.
   */
  area?: QueueKind | 'all';
}

const isOneOf = <T extends string>(list: readonly T[], value: unknown): value is T =>
  typeof value === 'string' && (list as readonly string[]).includes(value);

const text = (value: unknown) =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;

/** Reads the state from the URL; anything malformed is dropped (never an error page). */
export function parseInboxSearch(raw: Record<string, unknown>): InboxSearch {
  const search: InboxSearch = {};
  for (const key of [
    'conversationId',
    'channel',
    'label',
    'assignee',
    'team',
    'queue',
    'reception',
  ] as const) {
    const value = text(raw[key]);
    if (value) search[key] = value;
  }
  const folder = raw['folder'];
  if (isOneOf(CONVERSATION_FOLDERS, folder) && folder !== 'mine') search.folder = folder;
  // The folder of a queue needs its queue.
  if (search.folder === 'queue' && !search.queue) delete search.folder;
  if (search.folder !== 'triage') delete search.reception;
  if (isOneOf(ACTIVE_STATUSES, raw['status'])) search.status = raw['status'];
  if (isOneOf(CONVERSATION_PRIORITIES, raw['priority'])) search.priority = raw['priority'];
  if (raw['unread'] === true || raw['unread'] === 'true') search.unread = true;
  const q = text(raw['q']);
  if (q) search.q = q.slice(0, CONVERSATION_LIMITS.searchMax);
  if (raw['sort'] === 'waiting' || raw['sort'] === 'sla') search.sort = raw['sort'];
  if (isOneOf(SLA_FILTERS, raw['sla'])) search.sla = raw['sla'];
  if (isOneOf(WINDOW_FILTERS, raw['window'])) search.window = raw['window'];
  if (isOneOf(QUEUE_KINDS, raw['area']) || raw['area'] === 'all') search.area = raw['area'];
  if (search.folder === 'groups' && isOneOf(GROUP_FILTERS, raw['group'])) {
    search.group = raw['group'];
  }
  return search;
}

export function inboxFolder(search: InboxSearch): ConversationFolder {
  return search.folder ?? 'mine';
}

/** The folders of active conversations take the status filter (rule 14). */
export function folderTakesStatus(folder: ConversationFolder): boolean {
  return folder !== 'snoozed' && folder !== 'resolved';
}

/**
 * The area the list shows: only the one chosen in the filter (spec 056, C3 — no default by the
 * member's area any more). `all`, from older links, means every area.
 */
export function inboxArea(search: InboxSearch): QueueKind | undefined {
  return search.area === 'all' ? undefined : search.area;
}

/** The list query of the URL state (the search needs 2 characters). */
export function inboxListQuery(search: InboxSearch): ConversationListQuery {
  const folder = inboxFolder(search);
  const q = search.q && search.q.length >= CONVERSATION_LIMITS.searchMin ? search.q : undefined;
  const area = inboxArea(search);
  return {
    folder,
    ...(area && folder !== 'queue' && folder !== 'groups' ? { area } : {}),
    ...(search.status && folderTakesStatus(folder) ? { status: search.status } : {}),
    ...(search.channel ? { channelConnectionId: search.channel } : {}),
    ...(search.label ? { labelId: search.label } : {}),
    ...(search.assignee && folder === 'all' ? { assigneeMembershipId: search.assignee } : {}),
    ...(search.team ? { teamId: search.team } : {}),
    ...(search.queue ? { queueId: search.queue } : {}),
    ...(search.reception && folder === 'triage' ? { receptionId: search.reception } : {}),
    ...(search.priority ? { priority: search.priority } : {}),
    ...(search.sla && folderTakesStatus(folder) ? { sla: search.sla } : {}),
    ...(search.window ? { window: search.window } : {}),
    ...(search.group && folder === 'groups' ? { groupFilter: search.group } : {}),
    ...(search.unread ? { unread: true } : {}),
    ...(q ? { q } : {}),
    ...(search.sort ? { sort: search.sort } : {}),
  };
}

/** How many filters narrow the list (the folder, search and order aside). */
export function activeFilterCount(search: InboxSearch): number {
  return [
    search.status,
    search.channel,
    search.label,
    search.assignee,
    search.team,
    search.folder === 'queue' ? undefined : search.queue,
    search.priority,
    search.sla,
    search.window,
    search.group,
    search.unread,
    search.area,
  ].filter((value) => value !== undefined).length;
}

/** The same state without the filters (the folder, search and open conversation stay). */
export function withoutFilters(search: InboxSearch): InboxSearch {
  const { conversationId, folder, q, sort, queue, reception } = search;
  return {
    ...(conversationId ? { conversationId } : {}),
    ...(folder ? { folder } : {}),
    ...(folder === 'queue' && queue ? { queue } : {}),
    ...(folder === 'triage' && reception ? { reception } : {}),
    ...(q ? { q } : {}),
    ...(sort ? { sort } : {}),
  };
}
